// awards-service returns { "error": "..." } bodies. Every mapper here
// discriminates on status first; the 403 line is shared so the cycle and upload
// surfaces can't drift, while each keeps its own status cases and fallback.
const FORBIDDEN_MESSAGE = "Your admin account isn't authorized for Best in Dental yet.";

const statusOf = (error) => error?.response?.status;
const bodyOf = (error) => error?.response?.data?.error ?? '';

/**
 * Classifies a cycle create/edit failure into where it belongs and what it
 * should say: `{ field, message }`, where `field: 'dates'` marks a conflict the
 * admin resolves by changing the application window and the dialog can surface
 * on the date inputs, and `field: null` is a dialog-level problem.
 *
 * Every 409 this API raises is a window conflict — a retroactive shrink
 * (`ErrApplicationWindowShrunk`), dates that keep the cycle complete or regress
 * it to pending, or an overlap with another cycle (`ErrApplicationWindowOverlap`).
 * The bodies are admin-facing and name the offending field, so they are relayed
 * verbatim rather than pattern-matched. In particular the shared
 * "pending or active" phrase is now a *substring* of `ErrCycleNotEditable`'s
 * longer message (awards-service GOTU-5626), so matching on it would both
 * misclassify the window-shrunk sentinel and swallow the field name the amended
 * copy exists to give the admin.
 */
export const cycleSubmitError = (error) => {
  const status = statusOf(error);
  const body = bodyOf(error);

  if (status === 403) {
    return { field: null, message: FORBIDDEN_MESSAGE };
  }
  if (status === 409) {
    return { field: 'dates', message: body || 'This cycle can no longer be edited.' };
  }

  return { field: null, message: body || 'Something went wrong. Please try again.' };
};

export const uploadErrorMessage = (error) => {
  const status = statusOf(error);

  if (status === 413) return 'File exceeds the size limit.';
  if (status === 403) return FORBIDDEN_MESSAGE;
  if (status === 409) return 'This cycle is complete and can no longer be edited.';

  return bodyOf(error) || 'Upload failed. Please try again.';
};
